import React, {Dispatch} from 'react';
import {Box, Divider, IconButton, Typography} from '@mui/material';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import ArrIconButton from '../../../../common/arr-icon-button/ArrIconButton';
import {t} from 'i18next';

import {ReactComponent as ChevronIcon} from '../../../../../assets/images/chevron-base-icon.svg';
import './RedactionNavigation.scss';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {Trans} from 'react-i18next';
import ArrButton from '../../../../common/arr-button/ArrButton';

export interface RedactionNavigationProps {
  label: string;
  currentCount: number;
  totalCount: number;
  onChange: (newValue: number) => void;
  showNavigationButtons?: boolean;
  tooltipContent?: string;
  tooltipTitle?: string;
  isDefault?: boolean;
  isPartial?: boolean;
  isFailed?: boolean;
  fullWidthDivider?: boolean;
  setShowDeleteBatchModal?: Dispatch<React.SetStateAction<boolean>>;
  setBatchRedactionLabel?: Dispatch<React.SetStateAction<string>>;
}

const RedactionNavigation: React.FC<RedactionNavigationProps> = ({
  label,
  currentCount,
  totalCount: total,
  onChange,
  showNavigationButtons = true,
  isDefault = false,
  tooltipContent,
  tooltipTitle,
  isPartial = false,
  isFailed = false,
  fullWidthDivider = false,
  setShowDeleteBatchModal,
  setBatchRedactionLabel,
}) => {
  const testIdLabel = label.replace(/\s+/g, '-').toLowerCase();

  const handlePrev = () => {
    if (!showNavigationButtons) return;
    if (currentCount > -1) {
      onChange(currentCount - 1);
    }
  };

  const handleNext = () => {
    if (!showNavigationButtons) return;
    if (currentCount < total) {
      onChange(currentCount + 1);
    }
  };

  const handleBatchDelete = () => {
    // Check if the setters exist
    if (!setShowDeleteBatchModal || !setBatchRedactionLabel) return;

    // Check if the batch label is valid
    if (!label || label.trim() === '') return;

    setShowDeleteBatchModal(true);
    setBatchRedactionLabel(label);
  };

  return (
    <Box
      className="redaction-navigation-container"
      data-testid={`redaction-navigation-${testIdLabel}`}
    >
      <Box className="redaction-navigation">
        <Box
          display="flex"
          alignItems="center"
          data-testid="redaction-nav-text"
        >
          <Typography
            className={`typo-text label3_size radaction-label ${isFailed ? 'failed-label' : ''}`}
            data-testid={`redaction-navigation-label-default`}
          >
            {` ${isDefault ? '' : '['}${label}${isDefault ? '' : ']'}`}
          </Typography>
          {(isDefault || isFailed || isPartial) && (
            <ArrTooltipWrapper
              title={tooltipTitle}
              trigger={
                <IconButton
                  aria-label="Info"
                  className={`redaction-info-button info-button  ${isFailed ? 'failed-info-icon' : ''} ${isPartial ? 'partial-info-icon' : ''}`}
                >
                  <InfoIcon className="redaction-info-icon" />
                </IconButton>
              }
            >
              {isPartial ? (
                <Trans
                  i18nKey="docView.partialRedactionTooltip"
                  values={{arg0: label}}
                  components={{
                    strong: (
                      <strong
                        data-testid={`redaction-navigation-strong-partial`}
                      />
                    ),
                  }}
                />
              ) : isFailed ? (
                <Trans
                  i18nKey="docView.failedRedactionTooltip"
                  values={{arg0: label}}
                  components={{
                    strong: (
                      <strong
                        data-testid={`redaction-navigation-strong-failed`}
                      />
                    ),
                  }}
                />
              ) : (
                tooltipContent
              )}
            </ArrTooltipWrapper>
          )}
        </Box>
        {!isFailed && (
          <Box className="redaction-navigation-button-container">
            <Box>
              {!isDefault && (
                <ArrButton
                  variant="text"
                  className="navigation-delete-button typo-text buttonXS_size"
                  onClick={handleBatchDelete}
                  dataTestId="redaction-navigation-delete-button"
                >
                  Delete
                </ArrButton>
              )}
            </Box>
            <Box>
              <Box className="navigation-buttons-wrapper">
                <Typography
                  className="typo-text label3_size radaction-label"
                  data-testid={`redaction-navigation-current-count`}
                >
                  {currentCount} / {total}
                </Typography>

                <Box className="nav-button-container">
                  <ArrIconButton
                    testId="doc-redaction-previous-button"
                    className="previous-button"
                    size="small"
                    icon={<ChevronIcon className="previous-icon" />}
                    ariaLabel={t('docView.docPagination.previous')}
                    onClick={handlePrev}
                    disabled={currentCount === 0}
                    shape={currentCount === 0 ? 'none' : 'default'}
                  />
                  <ArrIconButton
                    testId="doc-redaction-next-button"
                    className="next-button"
                    size="small"
                    icon={<ChevronIcon className="next-icon" />}
                    ariaLabel={t('docView.docPagination.next')}
                    onClick={handleNext}
                    disabled={currentCount === total}
                    shape={currentCount === total ? 'none' : 'default'}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <Box className={fullWidthDivider ? '' : 'divider-padding'}>
        <Divider orientation="horizontal" flexItem className="arr-divider" />
      </Box>
    </Box>
  );
};

export default RedactionNavigation;
